import React, { useEffect, useRef } from 'react';
import { IToastConfig, useToastContext } from 'ToastProvider';
import { Heading6 } from 'components/theme/Typography/Typography';
import IconX from 'assets/icon-x';
import * as Styled from './styles';

interface IToastItemProps extends IToastConfig {
  current: number;
}

const Toast = () => {
  const { toastList } = useToastContext();

  return (
    <>
      <Styled.ToastRegionContainer
        role="status"
        aria-live="polite"
        aria-atomic="false"
      >
        {toastList
          .filter((toast) => toast.type === 'success')
          .map((toast, i) => (
            <ToastItem key={toast.id} {...toast} current={i + 1} />
          ))}
      </Styled.ToastRegionContainer>
      <Styled.ToastRegionContainer
        role="alert"
        aria-live="assertive"
        aria-atomic="false"
      >
        {toastList
          .filter((toast) => toast.type !== 'success')
          .map((toast, i) => (
            <ToastItem key={toast.id} {...toast} current={i + 1} />
          ))}
      </Styled.ToastRegionContainer>
    </>
  );
};

const ToastItem = ({
  id,
  position,
  title,
  type,
  message,
  current,
  duration,
}: IToastItemProps) => {
  const { deleteToast, extendToastDuration } = useToastContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timerId = setTimeout(() => {
      deleteToast(id);
    }, duration);

    return () => {
      clearTimeout(timerId);
    };
  }, [deleteToast, duration, id]);

  // Promote the toast into the browser's top layer so it paints above any
  // client-page stacking context. No-op (graceful fallback to fixed +
  // max z-index) when the Popover API is unsupported.
  useEffect(() => {
    const el = containerRef.current as
      | (HTMLDivElement & { showPopover?: () => void })
      | null;
    if (!el || typeof el.showPopover !== 'function') return;
    try {
      // styled-components v5 strips the unknown `popover` attribute, so set it
      // imperatively here — otherwise showPopover() throws and we never promote.
      el.setAttribute('popover', 'manual');
      if (!el.matches(':popover-open')) {
        el.showPopover();
      }
    } catch {
      // element not connected / already open — safe to ignore
    }
  }, []);

  return (
    <Styled.NotificationContainer
      ref={containerRef}
      key={id}
      className={`notification-container__${id} ${position}`}
      current={current}
    >
      <Styled.Notification className={position}>
        <Styled.NotificationButton
          type="button"
          onClick={() => deleteToast(id)}
          aria-label="Close notification"
        >
          <IconX color="var(--way-palette-black-70)" />
        </Styled.NotificationButton>

        <Styled.NotificationContent>
          <Styled.NotificationTextHeader>
            <Styled.NotificationImage
              isSuccess={type === 'success'}
              aria-hidden="true"
            >
              {type === 'success' ? (
                <Styled.StyledSuccessIcon />
              ) : (
                <Styled.StyledFailIcon />
              )}
            </Styled.NotificationImage>
            <Heading6>{title}</Heading6>
          </Styled.NotificationTextHeader>

          {message && <Heading6 className="message">{message}</Heading6>}

          <Styled.NotificationButton
            type="button"
            onClick={() => extendToastDuration(id, 20)}
            aria-label="Extend toast duration"
          >
            <Styled.StyledExtendIcon />
          </Styled.NotificationButton>
        </Styled.NotificationContent>
      </Styled.Notification>
    </Styled.NotificationContainer>
  );
};

export default Toast;
